/**
 * OverrideReasonModal.js
 *
 * FT-107: Modal for entering override reason when bypassing
 * pre-departure compliance blockers.
 *
 * Requires minimum 50 characters for reason.
 * Optional dispensation reference (e.g., MCA/DISP/2026/0042).
 * Auto-fills acknowledger from skipper name.
 *
 * FleetSkipper - Built for Mariners.
 */

import React, { useState, useCallback } from 'react';
import { X, AlertTriangle, ShieldX, Check } from 'lucide-react';

const MIN_REASON_LENGTH = 50;

const OverrideReasonModal = ({
  isOpen,
  onClose,
  onConfirm,
  blocker,          // { type: 'baseline'|'collective', key, name, crewName?, wbc3Ref? }
  skipperName       // Auto-filled
}) => {
  const [reason, setReason] = useState('');
  const [dispensationRef, setDispensationRef] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const remainingChars = MIN_REASON_LENGTH - reason.length;
  const isReasonValid = reason.length >= MIN_REASON_LENGTH;
  const canSubmit = isReasonValid && acknowledged && !isSubmitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onConfirm({
        reason: reason.trim(),
        dispensationRef: dispensationRef.trim() || null,
        acknowledgedBy: skipperName || 'Master',
        timestamp: new Date().toISOString()
      });
      // Reset form
      setReason('');
      setDispensationRef('');
      setAcknowledged(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, reason, dispensationRef, skipperName, onConfirm]);

  const handleClose = useCallback(() => {
    setReason('');
    setDispensationRef('');
    setAcknowledged(false);
    onClose();
  }, [onClose]);

  if (!isOpen || !blocker) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-orange-600 px-5 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <ShieldX className="h-5 w-5 text-white" />
            <div>
              <h2 className="text-base font-semibold text-white">
                Override Compliance Requirement
              </h2>
              <p className="text-sm text-orange-100">
                This will be logged in the official record
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-orange-500 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* What's being overridden */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-red-800">{blocker.name}</p>
                {blocker.crewName && (
                  <p className="text-sm text-red-700 mt-1">Crew: {blocker.crewName}</p>
                )}
                {blocker.wbc3Ref && (
                  <p className="text-xs text-red-600 mt-1 italic">{blocker.wbc3Ref}</p>
                )}
              </div>
            </div>
          </div>

          {/* Reason (required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Override <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter detailed justification for overriding this requirement (minimum 50 characters)..."
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none ${
                reason.length > 0 && !isReasonValid
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300'
              }`}
            />
            <div className="flex justify-between mt-1">
              <p className={`text-xs ${
                reason.length > 0 && !isReasonValid ? 'text-red-500' : 'text-gray-500'
              }`}>
                {remainingChars > 0
                  ? `${remainingChars} more characters required`
                  : `${reason.length} characters`
                }
              </p>
              {isReasonValid && (
                <Check className="h-4 w-4 text-green-500" />
              )}
            </div>
          </div>

          {/* Dispensation Reference (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dispensation Reference <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={dispensationRef}
              onChange={(e) => setDispensationRef(e.target.value)}
              placeholder="e.g., MCA/DISP/2026/0042"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Acknowledged by (auto-filled, read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Acknowledged by
            </label>
            <input
              type="text"
              value={skipperName || 'Master'}
              readOnly
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
            />
          </div>

          {/* Confirmation checkbox */}
          <label className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="h-5 w-5 mt-0.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
            />
            <div>
              <p className="text-sm font-medium text-amber-800">
                I understand this override will be permanently logged
              </p>
              <p className="text-xs text-amber-700 mt-1">
                This record will appear in the voyage report, logbook, and audit trail.
                It cannot be deleted or modified.
              </p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-200 flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3 px-4 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
              canSubmit
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin">...</span>
                <span>Processing</span>
              </>
            ) : (
              <>
                <ShieldX className="h-5 w-5" />
                <span>Confirm Override</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OverrideReasonModal;
